import type {
  BulkReportingLinesPayload,
  OrgChartNode,
  ReportingPerson,
  TemporaryGroup,
} from '@gokrew/models/reporting';
import { personName } from './reporting';

/**
 * "Manages a team?" (Add employee form and the employee's Manager section):
 * who will report to one person, picked three ways that can be combined.
 */
export interface ManageTeamSelection {
  /** Temporary groups (`groupKey`) taken over as a whole. */
  groupKeys: string[];
  /** Teams left without a manager by a departure (`OrphanTeam.key`). */
  orphanKeys: string[];
  /** "Replace a manager": their direct team moves over. */
  replaced: ReportingPerson | null;
  /** The replaced manager now reports to the new one (default) or stays put. */
  replacedJoins: boolean;
  /** One-off additions. */
  people: ReportingPerson[];
}

export const emptyManageTeamSelection = (): ManageTeamSelection => ({
  groupKeys: [],
  orphanKeys: [],
  replaced: null,
  replacedJoins: true,
  people: [],
});

export interface TeamMember {
  employeeId: string;
  name: string;
}

/**
 * Active employees left without a manager when theirs left (HR exception
 * view), grouped by the departure that caused it.
 */
export interface OrphanTeam {
  key: string;
  /** Who left, when the departure is still listed; null otherwise. */
  formerManagerName: string | null;
  members: TeamMember[];
}

export interface ManageTeamData {
  /** Waiting temporary groups, minus any the person already covers. */
  groups: TemporaryGroup[];
  orphanTeams: OrphanTeam[];
  /** Active employees with their current manager. */
  orgChart: OrgChartNode[];
}

export interface ManageTeamPlan {
  /** Groups moved through the assign endpoint (interim lines end properly). */
  groups: { group: TemporaryGroup; employeeIds: string[] }[];
  /**
   * The person's own line, applied before anyone moves under them: the new
   * hire's "Reports to", or the replaced manager's spot when the person is
   * promoted from inside that manager's team. Null leaves it untouched.
   */
  ownLine: {
    managerEmployeeId: string | null;
    managerName: string | null;
  } | null;
  /** Everyone else, moved through the bulk endpoint, in order. */
  moves: string[];
  /** The replaced manager's direct team as it will move (person excluded). */
  replacedTeam: TeamMember[];
  /** The person was in the replaced manager's team and was left out of it. */
  promotedFromTeam: boolean;
  /** Left out: they are above the person, so moving them would make a loop. */
  conflicts: TeamMember[];
  /** Display names of everyone the plan mentions. */
  names: Record<string, string>;
  /** People who will report to the person. */
  count: number;
}

export const EMPTY_PLAN: ManageTeamPlan = {
  groups: [],
  ownLine: null,
  moves: [],
  replacedTeam: [],
  promotedFromTeam: false,
  conflicts: [],
  names: {},
  count: 0,
};

/** Is `employeeId` anywhere under `managerId` in the current structure? */
const isUnder = (
  employeeId: string,
  managerId: string,
  managerOf: Map<string, string | null>
) => {
  const seen = new Set<string>();
  let current = managerOf.get(employeeId) ?? null;
  while (current && !seen.has(current)) {
    if (current === managerId) return true;
    seen.add(current);
    current = managerOf.get(current) ?? null;
  }
  return false;
};

interface BuildPlanInput {
  selection: ManageTeamSelection;
  data: ManageTeamData;
  /** The person taking the team; null for a new hire not saved yet. */
  managerId: string | null;
  /** New hire only: their own manager ("Reports to"). */
  reportsTo?: ReportingPerson | null;
}

/**
 * Turns the selection into the moves to apply. The person never reports to
 * themselves; someone promoted from inside the replaced manager's team takes
 * that manager's spot first; anyone above the person in the final structure
 * is left out (moving them would make a loop) and listed in `conflicts`.
 */
export const buildManageTeamPlan = ({
  selection,
  data,
  managerId,
  reportsTo = null,
}: BuildPlanInput): ManageTeamPlan => {
  const names: Record<string, string> = {};
  const managerOf = new Map<string, string | null>();
  for (const node of data.orgChart) {
    managerOf.set(node.employeeId, node.managerEmployeeId);
    names[node.employeeId] = personName(node);
  }
  const notSelf = (id: string) => id !== managerId;

  // 1. Temporary groups keep their own path (the assign endpoint).
  const grouped = new Set<string>();
  const groupPicks = data.groups
    .filter((group) => selection.groupKeys.includes(group.groupKey))
    .map((group) => {
      const employeeIds = group.members
        .map((member) => {
          names[member.employeeId] = member.name;
          return member.employeeId;
        })
        .filter(notSelf);
      employeeIds.forEach((id) => grouped.add(id));
      return { group, employeeIds };
    });

  // 2. Everyone else goes through the bulk endpoint, once each.
  const moves: string[] = [];
  const add = (id: string) => {
    if (notSelf(id) && !grouped.has(id) && !moves.includes(id)) moves.push(id);
  };

  let replacedTeam: TeamMember[] = [];
  let promotedFromTeam = false;
  let ownLine: ManageTeamPlan['ownLine'] = reportsTo
    ? {
        managerEmployeeId: reportsTo.employeeId,
        managerName: personName(reportsTo),
      }
    : null;
  if (reportsTo) names[reportsTo.employeeId] = personName(reportsTo);

  const replaced = selection.replaced;
  if (replaced && replaced.employeeId !== managerId) {
    names[replaced.employeeId] = personName(replaced);
    // Direct reports on a permanent line; a group the manager covers is
    // offered on its own above.
    const direct = data.orgChart.filter(
      (node) =>
        node.managerEmployeeId === replaced.employeeId && !node.isInterim
    );
    promotedFromTeam =
      !!managerId && isUnder(managerId, replaced.employeeId, managerOf);
    replacedTeam = direct
      .filter((node) => notSelf(node.employeeId))
      .map((node) => ({
        employeeId: node.employeeId,
        name: personName(node),
      }));
    replacedTeam.forEach((member) => add(member.employeeId));
    if (selection.replacedJoins) add(replaced.employeeId);
    if (promotedFromTeam) {
      // Promoted from inside the team: they take the replaced manager's spot.
      const above = managerOf.get(replaced.employeeId) ?? null;
      ownLine = {
        managerEmployeeId: above,
        managerName: above ? (names[above] ?? null) : null,
      };
    }
  }

  for (const team of data.orphanTeams) {
    if (!selection.orphanKeys.includes(team.key)) continue;
    for (const member of team.members) {
      names[member.employeeId] = member.name;
      add(member.employeeId);
    }
  }
  for (const person of selection.people) {
    names[person.employeeId] = personName(person);
    add(person.employeeId);
  }

  // 3. Nobody above the person (in the final structure) may move under them.
  const moving = new Set([...grouped, ...moves]);
  const conflicts: TeamMember[] = [];
  const seen = new Set<string>();
  let current = ownLine
    ? ownLine.managerEmployeeId
    : managerId
      ? (managerOf.get(managerId) ?? null)
      : null;
  while (current && !seen.has(current)) {
    seen.add(current);
    if (moving.has(current)) {
      moving.delete(current);
      conflicts.push({
        employeeId: current,
        name: names[current] ?? current,
      });
    }
    current = managerOf.get(current) ?? null;
  }
  const keep = (id: string) => moving.has(id);

  return {
    groups: groupPicks
      .map((pick) => ({ ...pick, employeeIds: pick.employeeIds.filter(keep) }))
      .filter((pick) => pick.employeeIds.length > 0),
    ownLine,
    moves: moves.filter(keep),
    replacedTeam,
    promotedFromTeam,
    conflicts,
    names,
    count: moving.size,
  };
};

/**
 * The bulk items, in a safe order: the person's own line first (so they are
 * out of anyone's team before that team moves under them), then everyone
 * else. With every move pointing at the person and nobody above them moving,
 * no intermediate step can form a loop.
 */
export const toBulkItems = (
  plan: ManageTeamPlan,
  managerEmployeeId: string
): BulkReportingLinesPayload['items'] => [
  ...(plan.ownLine
    ? [
        {
          employeeId: managerEmployeeId,
          managerEmployeeId: plan.ownLine.managerEmployeeId,
        },
      ]
    : []),
  ...plan.moves.map((employeeId) => ({ employeeId, managerEmployeeId })),
];

/** "Rick's former reports" teams from the HR exception view's list. */
export const toOrphanTeams = (
  rows: {
    employeeId: string;
    firstName: string;
    lastName: string;
    handoverId: string | null;
  }[],
  departingByHandover: Record<string, string | null>
): OrphanTeam[] => {
  const teams = new Map<string, OrphanTeam>();
  for (const row of rows) {
    const key = row.handoverId ?? 'none';
    const team = teams.get(key) ?? {
      key,
      formerManagerName: row.handoverId
        ? (departingByHandover[row.handoverId] ?? null)
        : null,
      members: [],
    };
    team.members.push({
      employeeId: row.employeeId,
      name: `${row.firstName} ${row.lastName}`.trim(),
    });
    teams.set(key, team);
  }
  return [...teams.values()];
};
